import { Injectable } from '@angular/core';
import { ServiceBase } from 'app/shared/serviceBase';
import { AuthenticationService } from 'app/shared/services/authentication-service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UserAuditListModel } from '../models/user-audit-list.model';
import { AuditTypeListModel } from '../models/audit-type-list.model';
import { CreateUserAuditModel } from '../models/create-user-audit.model';
import { CompanyAuditListModel } from 'app/company/models/company-audit-list.model';
import { QuestionModel } from '../models/question.model';
import { UserAuditModel } from '../models/user-audit.model';
import { AuditFormModel } from '../models/audit-form.model';
import { AnswerModel } from '../models/answer.model';
import { CompanyAuditModel } from 'app/company/models/company-audit.model';
import { DocumentModel } from '../models/document.model';
import * as FileSaver from 'file-saver';
import { QuestionBankModel } from '../models/question-bank.model';
import { MyAuditListComponent } from '../component/my-audit-list/my-audit-list.component';
import { MyAuditListModel } from '../models/my-audit-list.model';

@Injectable()
export class UserAuditService extends ServiceBase {
    private commonUrl: string = this.url + '/UserAudit/';
    constructor(
        private _http: HttpClient,
        private _authentication: AuthenticationService
    ) {
        super();
    }

    get(userAuditId: string): Observable<UserAuditModel> {
        const completeUrl: string = this.commonUrl + userAuditId;
        return this._http
            .get<UserAuditModel>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAllUserAudits(companyAuditId: string): Observable<UserAuditListModel[]> {
        const completeUrl: string = this.commonUrl + 'user-audit/' + companyAuditId;
        return this._http
            .get<UserAuditListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAllCompanyAudits(): Observable<CompanyAuditListModel[]> {
        const completeUrl: string = this.commonUrl + 'company-audits';
        return this._http
            .get<CompanyAuditListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getMyUserAudits(): Observable<MyAuditListModel[]> {
        const completeUrl: string = this.commonUrl + 'my-audits';
        return this._http
            .get<MyAuditListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getCompanyAudit(companyAuditId: string): Observable<CompanyAuditModel> {
        const completeUrl: string = this.commonUrl + 'company-audit/' + companyAuditId;
        return this._http
            .get<CompanyAuditModel>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAuditTypes(): Observable<AuditTypeListModel[]> {
        const completeUrl: string = this.commonUrl + 'audit-types';
        return this._http
            .get<AuditTypeListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    createAudit(model: CreateUserAuditModel): Observable<string> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    getQuestions(userAuditId: string): Observable<QuestionBankModel[]> {
        const completeUrl: string = this.commonUrl + 'question/' + userAuditId;
        return this._http
            .get<QuestionBankModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAnswers(userAuditId: string): Observable<QuestionBankModel[]> {
        const completeUrl: string = this.commonUrl + 'answer/' + userAuditId;
        return this._http
            .get<QuestionBankModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getDocuments(userAuditId: string): Observable<DocumentModel[]> {
        const completeUrl: string = this.commonUrl + 'documents/' + userAuditId;
        return this._http
            .get<DocumentModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getDocument(id: string): Observable<HttpResponse<Blob>> {
        const completeUrl: string = this.commonUrl + 'document/' + id;
        return this._http.get(completeUrl, { responseType: 'blob', observe: 'response' })
            .pipe(catchError(this.handleError));
    }

    downloadGeneratedDocument(documentId: string): void {
        this.getDocument(documentId).subscribe(
            response => {
                const fileName = response.headers.get('content-disposition').split(';')[1].trim().split('=')[1].replace(/"/g, '');
                FileSaver.saveAs(response.body, fileName);
            }
        )
    }

    createUserAudit(model: AuditFormModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'user-audit';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    deleteDocument(id: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'document/' + id;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }
}
